import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { differenceInYears } from "date-fns";
import { useAuth } from "../hooks/use-auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, UserPlus } from "lucide-react";
import { siteConfig } from "../mock";

export default function RegisterPage() {
  const { register, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/area-socio/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Athlete fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [phone, setPhone] = useState("");
  const [nif, setNif] = useState("");

  // Guardian fields
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianPhone, setGuardianPhone] = useState("");
  const [guardianRelation, setGuardianRelation] = useState("Pai");

  const isMinor = useMemo(() => {
    if (!birthDate) return false;
    try {
      return differenceInYears(new Date(), new Date(birthDate)) < 18;
    } catch {
      return false;
    }
  }, [birthDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== passwordConfirm) {
      alert("As passwords não coincidem.");
      return;
    }

    const payload = {
      email,
      password,
      name,
      birth_date: birthDate,
      gender,
      phone,
      nif,
    };

    if (isMinor) {
      payload.guardian = {
        name: guardianName,
        email: guardianEmail,
        phone: guardianPhone,
        relation: guardianRelation,
      };
    }

    const ok = await register(payload);
    if (ok) {
      navigate("/area-socio/login");
    }
  };

  return (
    <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-lg mx-auto px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-brand-700 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao site
        </Link>

        <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8">
          <div className="text-center mb-8">
            <img
              src={siteConfig.logoBlue}
              alt="CATV"
              className="h-10 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-stone-900">
              Criar Conta
            </h1>
            <p className="text-stone-500 text-sm mt-1">
              Preenche os teus dados para criares a tua área de atleta.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Dados do Atleta */}
            <fieldset className="border border-stone-200 rounded-xl p-5">
              <legend className="text-sm font-bold text-brand-700 px-2 uppercase tracking-wider">
                Dados do Atleta
              </legend>
              <div className="space-y-4 mt-1">
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-1">
                    Nome completo *
                  </label>
                  <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="O teu nome completo" />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-1">
                    Email *
                  </label>
                  <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="o.teu@email.pt" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Password *
                    </label>
                    <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mín. 6 caracteres" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Confirmar *
                    </label>
                    <Input required type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="Repete a password" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Data de nascimento *
                    </label>
                    <Input required type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Género
                    </label>
                    <select
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    >
                      <option value="">Seleciona</option>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Telefone
                    </label>
                    <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="912 345 678" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      NIF
                    </label>
                    <Input value={nif} onChange={(e) => setNif(e.target.value)} placeholder="123456789" />
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Encarregado de Educação (só se menor) */}
            {isMinor && (
              <fieldset className="border border-amber-200 rounded-xl p-5 bg-amber-50/30">
                <legend className="text-sm font-bold text-amber-700 px-2 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Encarregado de Educação
                </legend>
                <p className="text-xs text-amber-700 mb-3">
                  Obrigatório para atletas menores de 18 anos.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Nome do EE *
                    </label>
                    <Input required={isMinor} value={guardianName} onChange={(e) => setGuardianName(e.target.value)} placeholder="Nome do encarregado" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-stone-700 block mb-1">
                        Email do EE *
                      </label>
                      <Input required={isMinor} type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)} placeholder="ee@email.pt" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-stone-700 block mb-1">
                        Telefone do EE *
                      </label>
                      <Input required={isMinor} type="tel" value={guardianPhone} onChange={(e) => setGuardianPhone(e.target.value)} placeholder="912 345 678" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 block mb-1">
                      Relação *
                    </label>
                    <select
                      value={guardianRelation}
                      onChange={(e) => setGuardianRelation(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    >
                      <option value="Pai">Pai</option>
                      <option value="Mãe">Mãe</option>
                      <option value="Tutor legal">Tutor legal</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                </div>
              </fieldset>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-ember-400 to-ember-600 hover:from-ember-500 hover:to-ember-700 disabled:opacity-60 text-white rounded-full py-6 text-base font-semibold"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {loading ? "A criar conta..." : "Criar Conta"}
            </Button>
          </form>

          <p className="text-center text-sm text-stone-500 mt-6">
            Já tens conta?{" "}
            <Link
              to="/area-socio/login"
              className="text-brand-700 hover:text-brand-800 font-semibold"
            >
              Inicia sessão
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
